import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Alert,
  Divider,
  Paper,
  Button,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageContainer from '../components/shared/pageContainer';
import PageHeader from '../components/shared/page-header';
import { DetailSkeleton } from '../components/shared/skeletons';
import { useCustomPageController } from './context/custom-page-controller';
import { useCustomPageState } from './context/custom-page-context';

export function CustomPageComponent() {
  const { idArticle } = useParams<{ idArticle: string }>();
  const navigate = useNavigate();
  const { loadArticle } = useCustomPageController();
  const { detail, loading, error } = useCustomPageState();

  useEffect(() => {
    if (idArticle) {
      loadArticle(parseInt(idArticle));
    }
  }, [idArticle]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  if (loading) {
    return <DetailSkeleton hasImage={false} hasSections={1} />;
  }

  if (error) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <PageHeader
            title="Página Customizada"
            breadcrumbs={[
              { label: 'Página Customizada' },
            ]}
          />
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              size="large"
            >
              Voltar para Início
            </Button>
          </Box>
        </Container>
      </PageContainer>
    );
  }

  if (!detail) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <PageHeader
            title="Página Customizada"
            breadcrumbs={[
              { label: 'Página Customizada' },
            ]}
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            Artigo não encontrado.
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              size="large"
            >
              Voltar para Início
            </Button>
          </Box>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <PageHeader
          title={detail.title || 'Página Customizada'}
          breadcrumbs={[
            { label: detail.title || 'Página Customizada' },
          ]}
        />

        <Paper
          elevation={3}
          sx={{
            p: 4,
            mt: 2,
            borderRadius: 2,
          }}
        >
          {/* Cabeçalho com data de publicação */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'text.secondary',
              }}
            >
              <CalendarTodayIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2">
                Publicado em {formatDate(detail.createdDate)}
              </Typography>
            </Box>
            {detail.modifiedDate && (
              <>
                <Divider orientation="vertical" flexItem />
                <Typography variant="body2" color="text.secondary">
                  Atualizado em {formatDate(detail.modifiedDate)}
                </Typography>
              </>
            )}
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Conteúdo do artigo com normalização de HTML */}
          {detail.content && (
            <Box
              sx={{
                // Estilos para normalização de conteúdo HTML
                '& *': {
                  fontFamily: 'Roboto, Arial, sans-serif !important',
                  maxWidth: '100%',
                },
                '& p': {
                  mb: 2,
                  lineHeight: 1.8,
                  fontSize: '1.1rem !important',
                  color: 'text.primary',
                  margin: '0 0 16px 0',
                },
                '& span': {
                  fontSize: 'inherit !important',
                  lineHeight: 'inherit !important',
                },
                '& div': {
                  maxWidth: '100%',
                },
                '& img': {
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: 1,
                  my: 2,
                  display: 'block',
                },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  mt: 3,
                  mb: 2,
                  fontWeight: '600 !important',
                  color: 'primary.main',
                  lineHeight: '1.4 !important',
                },
                '& h1': { fontSize: '2rem !important' },
                '& h2': { fontSize: '1.75rem !important' },
                '& h3': { fontSize: '1.5rem !important' },
                '& h4': { fontSize: '1.25rem !important' },
                '& h5': { fontSize: '1.1rem !important' },
                '& h6': { fontSize: '1rem !important' },
                '& ul, & ol': {
                  ml: 3,
                  mb: 2,
                  pl: 2,
                },
                '& li': {
                  mb: 1,
                  lineHeight: 1.8,
                  fontSize: '1.1rem !important',
                },
                '& strong, & b': {
                  fontWeight: '600 !important',
                },
                '& em, & i': {
                  fontStyle: 'italic',
                },
                '& a': {
                  color: 'primary.main',
                  textDecoration: 'underline',
                  '&:hover': {
                    color: 'primary.dark',
                  },
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  mb: 2,
                  overflow: 'auto',
                  display: 'block',
                },
                '& td, & th': {
                  border: '1px solid',
                  borderColor: 'divider',
                  padding: '12px',
                  textAlign: 'left',
                  fontSize: '1rem !important',
                },
                '& th': {
                  backgroundColor: 'grey.100',
                  fontWeight: '600 !important',
                },
                '& blockquote': {
                  borderLeft: '4px solid',
                  borderColor: 'primary.main',
                  pl: 2,
                  ml: 0,
                  my: 2,
                  py: 1,
                  fontStyle: 'italic',
                  backgroundColor: 'grey.50',
                },
                '& pre': {
                  backgroundColor: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  mb: 2,
                },
                '& code': {
                  fontFamily: 'monospace !important',
                  fontSize: '0.9rem !important',
                  backgroundColor: 'grey.100',
                  px: 0.5,
                  borderRadius: 0.5,
                },
                '& hr': {
                  my: 3,
                  borderColor: 'divider',
                },
              }}
              dangerouslySetInnerHTML={{ __html: detail.content }}
            />
          )}

          {!detail.content && (
            <Alert severity="info">
              Este artigo não possui conteúdo disponível.
            </Alert>
          )}
        </Paper>

        {/* Botão de voltar - centralizado no final */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4,
            mb: 3,
          }}
        >
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            size="large"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5,
            }}
          >
            Voltar para Início
          </Button>
        </Box>
      </Container>
    </PageContainer>
  );
}
